import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Spinner } from '../components/common/LoadingComponents';
import api from '../services/api';
import '../styles/Login.css';

function OAuthCallback({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth error
      if (errorParam) {
        setError(errorDescription || 'Authentication was cancelled or failed');
        setProcessing(false);
        return;
      }

      if (!code || !state) {
        setError('Invalid OAuth callback - missing parameters');
        setProcessing(false);
        return;
      }

      // Get stored OAuth state
      const storedState = sessionStorage.getItem('oauth_state');
      const storedProvider = sessionStorage.getItem('oauth_provider');

      if (!storedState || storedState !== state) {
        setError('Invalid state token - possible CSRF attack');
        setProcessing(false);
        return;
      }

      // Clear stored state
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_provider');

      try {
        // Exchange code for tokens and authenticate
        const response = await api.post('/oauth/callback', {
          code,
          state,
          provider: storedProvider
        });

        const { user, household, token, isNewUser } = response.data;

        // Save to localStorage
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        if (household) {
          localStorage.setItem('household', JSON.stringify(household));
        }

        // Call parent login handler
        onLogin(user, household);

        // Redirect based on whether user is new or has no household
        if (isNewUser || !household) {
          // New user needs to create/join a household
          navigate('/household?setup=true');
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        
        if (err.response?.status === 503 && err.response?.data?.maintenanceMode) {
          setError(err.response.data.message);
        } else if (err.response?.status === 403) {
          setError(err.response?.data?.error || 'Registration is disabled. Please contact an administrator.');
        } else {
          setError(err.response?.data?.error || 'Authentication failed');
        }
        setProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, onLogin]);

  if (processing) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="oauth-processing">
            <Spinner size="xl" />
            <h2>Signing you in...</h2>
            <p>Please wait while we complete your authentication.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="oauth-error">
          <AlertTriangle size={48} className="error-icon" />
          <h2>Authentication Failed</h2>
          <p className="error-message">{error}</p>
          <button 
            className="btn-primary" 
            onClick={() => window.location.href = '/login'}
          >
            Return to Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default OAuthCallback;
